import { useRef, useState } from "react";
import {
  Box,
  CircularProgress,
  IconButton,
  LinearProgress,
  Stack,
  TextField,
  Tooltip,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { useSendChatMessageMutation } from "../../../../app/communicationApiSlice";
import { useUploadFileMutation } from "../../../../app/utilsApiSlice";
import toast from "../../../../helpers/toast";

function AttachPreview({ file, onRemove }) {
  const isImage = file.type.startsWith("image/");
  const isVideo = file.type.startsWith("video/");
  const preview = isImage ? URL.createObjectURL(file) : null;

  return (
    <Box
      sx={{
        position: "relative",
        width: 56,
        height: 56,
        borderRadius: 1,
        overflow: "hidden",
        border: "1px solid",
        borderColor: "divider",
        flexShrink: 0,
      }}
    >
      {isImage && preview && (
        <Box
          component="img"
          src={preview}
          alt=""
          sx={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      )}
      {!isImage && (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            bgcolor: "grey.100",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <AttachFileOutlinedIcon fontSize="small" color="disabled" />
        </Box>
      )}
      <IconButton
        size="small"
        onClick={onRemove}
        sx={{
          position: "absolute",
          top: 1,
          right: 1,
          bgcolor: "rgba(0,0,0,0.55)",
          color: "#fff",
          p: 0.15,
          "&:hover": { bgcolor: "rgba(0,0,0,0.8)" },
        }}
      >
        <CloseIcon sx={{ fontSize: 12 }} />
      </IconButton>
    </Box>
  );
}

export default function MessageComposer({ conversationId, onSent }) {
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [sendMessage, { isLoading: sending }] = useSendChatMessageMutation();
  const [uploadFile] = useUploadFileMutation();
  const mediaRef = useRef(null);
  const fileRef = useRef(null);

  const handleMediaSelect = (e) => {
    const picked = Array.from(e.target.files || []).slice(0, 5 - files.length);
    setFiles((prev) => [...prev, ...picked]);
    e.target.value = "";
  };

  const handleFileSelect = (e) => {
    const picked = Array.from(e.target.files || []).slice(0, 5 - files.length);
    setFiles((prev) => [...prev, ...picked]);
    e.target.value = "";
  };

  const handleSend = async () => {
    if (!text.trim() && files.length === 0) return;
    setUploading(true);
    const mediaUrls = [];

    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const res = await uploadFile(fd).unwrap();
        if (res?.url) mediaUrls.push(res.url);
      }
    } catch {
      toast.error("File upload failed");
      setUploading(false);
      return;
    }

    setUploading(false);

    try {
      await sendMessage({
        conversationId,
        body: text.trim(),
        data: mediaUrls.length ? { media_urls: mediaUrls } : undefined,
      }).unwrap();
      setText("");
      setFiles([]);
      onSent?.();
    } catch (err) {
      toast.error(err?.data?.message || "Could not send message");
    }
  };

  const isSubmitting = uploading || sending;

  return (
    <Box
      sx={{
        borderTop: 1,
        borderColor: "divider",
        p: 1.5,
        bgcolor: "background.paper",
      }}
    >
      {/* Attachment previews */}
      {files.length > 0 && (
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
          {files.map((f, i) => (
            <AttachPreview
              key={i}
              file={f}
              onRemove={() => setFiles((prev) => prev.filter((_, idx) => idx !== i))}
            />
          ))}
        </Box>
      )}

      {isSubmitting && <LinearProgress sx={{ mb: 1, borderRadius: 1 }} />}

      <Stack direction="row" spacing={1} alignItems="flex-end">
        <Tooltip title="Add photo / video">
          <IconButton
            size="small"
            onClick={() => mediaRef.current?.click()}
            disabled={files.length >= 5}
          >
            <ImageOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Attach file">
          <IconButton
            size="small"
            onClick={() => fileRef.current?.click()}
            disabled={files.length >= 5}
          >
            <AttachFileOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <TextField
          fullWidth
          size="small"
          multiline
          maxRows={4}
          placeholder="Type a message…"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
        />

        <IconButton
          color="primary"
          onClick={handleSend}
          disabled={isSubmitting || (!text.trim() && files.length === 0)}
          sx={{
            bgcolor: "primary.main",
            color: "primary.contrastText",
            "&:hover": { bgcolor: "primary.dark" },
            "&:disabled": { bgcolor: "action.disabledBackground" },
            flexShrink: 0,
          }}
        >
          {isSubmitting ? (
            <CircularProgress size={18} color="inherit" />
          ) : (
            <SendIcon fontSize="small" />
          )}
        </IconButton>
      </Stack>

      <input
        ref={mediaRef}
        type="file"
        accept="image/*,video/*"
        multiple
        style={{ display: "none" }}
        onChange={handleMediaSelect}
      />
      <input
        ref={fileRef}
        type="file"
        accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.zip"
        multiple
        style={{ display: "none" }}
        onChange={handleFileSelect}
      />
    </Box>
  );
}
